import type { Context, FC, ReactNode } from 'react'
import type { KnowledgeDraft, NotificationItem } from '@/types'

import {
    createContext,
    useCallback,
    useContext,
    useMemo,
    useRef,
    useState
} from 'react'

import { CircleAlert, CircleCheck, Info, X } from 'lucide-react'
import { cn } from '@/lib/utils'
import { usePersistentState } from '@/hooks/use-persistent-state'
import { NOTIFICATIONS } from '@/constants/notifications'

const DEFAULT_FAVORITES = ['security-predeploy-checklist', 'payment-connectivity-errors']

type FavoritesValue = {
    favorites: string[]
    ready: boolean
    isFavorite: (slug: string) => boolean
    toggleFavorite: (slug: string) => boolean
    removeFavorite: (slug: string) => void
}

type DraftsValue = {
    drafts: KnowledgeDraft[]
    ready: boolean
    saveDraft: (draft: KnowledgeDraft) => void
    removeDraft: (id: string) => void
}

type PreferencesValue = {
    hints: boolean
    setHints: (value: boolean) => void
    sidebarCollapsed: boolean
    setSidebarCollapsed: (value: boolean) => void
}

type NotificationsValue = {
    items: NotificationItem[]
    unreadCount: number
    markRead: (id: string) => void
    markAllRead: () => void
}

type ToastTone = 'success' | 'info' | 'error'

type ToastEntry = {
    id: number
    message: string
    tone: ToastTone
}

type ToastValue = {
    notify: (message: string, tone?: ToastTone) => void
}

const FavoritesContext = createContext<FavoritesValue | null>(null)
const DraftsContext = createContext<DraftsValue | null>(null)
const PreferencesContext = createContext<PreferencesValue | null>(null)
const NotificationsContext = createContext<NotificationsValue | null>(null)
const ToastContext = createContext<ToastValue | null>(null)

const useRequiredContext = <T,>(context: Context<T | null>, name: string): T => {
    const value = useContext(context)

    if (!value) throw new Error(`${name} must be used inside AppProviders`)

    return value
}

export const useFavorites = (): FavoritesValue => useRequiredContext(FavoritesContext, 'useFavorites')
export const useDrafts = (): DraftsValue => useRequiredContext(DraftsContext, 'useDrafts')
export const usePreferences = (): PreferencesValue => useRequiredContext(PreferencesContext, 'usePreferences')
export const useNotifications = (): NotificationsValue => useRequiredContext(NotificationsContext, 'useNotifications')
export const useToast = (): ToastValue => useRequiredContext(ToastContext, 'useToast')

const TOAST_ICONS = {
    success: CircleCheck,
    info: Info,
    error: CircleAlert
}

const TOAST_TONES: Record<ToastTone, string> = {
    success: 'text-success',
    info: 'text-primary',
    error: 'text-destructive'
}

interface AppProvidersProps {
    children: ReactNode
}

export const AppProviders: FC<AppProvidersProps> = ({ children }): ReactNode => {
    const [favorites, setFavorites, favoritesReady] = usePersistentState<string[]>('km-favorites', DEFAULT_FAVORITES)
    const [drafts, setDrafts, draftsReady] = usePersistentState<KnowledgeDraft[]>('km-drafts', [])
    const [hints, setHints] = usePersistentState<boolean>('km-confluence-hints', false)
    const [sidebarCollapsed, setSidebarCollapsed] = usePersistentState<boolean>('km-sidebar-collapsed', false)
    const [notifications, setNotifications] = useState<NotificationItem[]>(NOTIFICATIONS)
    const [toasts, setToasts] = useState<ToastEntry[]>([])
    const toastCounter = useRef(0)

    const dismissToast = useCallback((id: number) => {
        setToasts(current => current.filter(toast => toast.id !== id))
    }, [])

    const notify = useCallback((message: string, tone: ToastTone = 'success') => {
        toastCounter.current += 1

        const id = toastCounter.current

        setToasts(current => [...current.slice(-2), { id, message, tone }])
        window.setTimeout(() => dismissToast(id), 3200)
    }, [dismissToast])

    const favoritesValue = useMemo<FavoritesValue>(() => ({
        favorites,
        ready: favoritesReady,
        isFavorite: (slug: string) => favorites.includes(slug),
        toggleFavorite: (slug: string) => {
            const exists = favorites.includes(slug)

            setFavorites(current => exists ? current.filter(item => item !== slug) : [slug, ...current])

            return !exists
        },
        removeFavorite: (slug: string) => setFavorites(current => current.filter(item => item !== slug))
    }), [favorites, favoritesReady, setFavorites])

    const draftsValue = useMemo<DraftsValue>(() => ({
        drafts,
        ready: draftsReady,
        saveDraft: (draft: KnowledgeDraft) => {
            setDrafts(current => {
                const others = current.filter(item => item.id !== draft.id)

                return [draft, ...others]
            })
        },
        removeDraft: (id: string) => setDrafts(current => current.filter(item => item.id !== id))
    }), [drafts, draftsReady, setDrafts])

    const preferencesValue = useMemo<PreferencesValue>(() => ({
        hints,
        setHints,
        sidebarCollapsed,
        setSidebarCollapsed
    }), [hints, setHints, sidebarCollapsed, setSidebarCollapsed])

    const notificationsValue = useMemo<NotificationsValue>(() => ({
        items: notifications,
        unreadCount: notifications.filter(item => item.unread).length,
        markRead: (id: string) => setNotifications(current => current.map(item => item.id === id ? { ...item, unread: false } : item)),
        markAllRead: () => setNotifications(current => current.map(item => ({ ...item, unread: false })))
    }), [notifications])

    const toastValue = useMemo<ToastValue>(() => ({ notify }), [notify])

    return (
        <ToastContext.Provider value={toastValue}>
            <PreferencesContext.Provider value={preferencesValue}>
                <NotificationsContext.Provider value={notificationsValue}>
                    <FavoritesContext.Provider value={favoritesValue}>
                        <DraftsContext.Provider value={draftsValue}>
                            {children}
                        </DraftsContext.Provider>
                    </FavoritesContext.Provider>
                </NotificationsContext.Provider>
            </PreferencesContext.Provider>

            <div
                className='pointer-events-none fixed inset-x-4 bottom-4 z-[70] flex flex-col items-center gap-2 sm:inset-x-auto sm:start-6 sm:bottom-6 sm:items-start'
                aria-live='polite'
                aria-atomic='false'
            >
                {toasts.map(toast => {
                    const Icon = TOAST_ICONS[toast.tone]

                    return (
                        <div
                            key={toast.id}
                            role='status'
                            className='pointer-events-auto flex w-full max-w-sm items-center gap-3 rounded-lg bg-navy px-4 py-3 text-sm text-white shadow-pop animate-in fade-in slide-in-from-bottom-2 duration-200'
                        >
                            <Icon className={cn('size-5 shrink-0', TOAST_TONES[toast.tone])} aria-hidden='true' />

                            <span className='flex-1'>{toast.message}</span>

                            <button
                                type='button'
                                onClick={() => dismissToast(toast.id)}
                                className='flex size-7 cursor-pointer items-center justify-center rounded-md text-white/70 transition-colors hover:bg-white/10 hover:text-white'
                                aria-label='بستن پیام'
                            >
                                <X className='size-4' aria-hidden='true' />
                            </button>
                        </div>
                    )
                })}
            </div>
        </ToastContext.Provider>
    )
}
